import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { Box, Button, IconButton, MenuItem, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { atom, useAtomValue } from "jotai";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { OverviewForm } from "../../../../hooks/useOverview.ts";
import ContentPaper from "../../../../Layouts/sz-content-paper/ContentPaper.tsx";

export const totalCountAtom = atom<number>(0);

const paginationOptions: Array<{ label: string; value: number }> = [
	{ label: "5", value: 5 },
	{ label: "10", value: 10 },
	{ label: "25", value: 25 },
	{ label: "50", value: 50 },
	{ label: "100", value: 100 },
];

export const ResultsPagination: React.FC<{ overviewForm: OverviewForm; totalCount: number }> = ({ overviewForm, totalCount }) => {
	const [open, setOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement | null>(null); // Reference to the Paper component
	const buttonRef = useRef<HTMLButtonElement | null>(null); // Reference to the Button component

	const handleOpen = () => {
		setOpen(!open);
	};

	const handleClose = () => {
		setOpen(false);
	};

	// Close the menu when clicking outside of the button or menu
	useEffect(() => {
		// @ts-expect-error
		const handleClickAway = (event) => {
			// Check if the click is outside the button and menu
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target)
			) {
				// Close the menu if clicked outside
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickAway);

		return () => {
			document.removeEventListener("mousedown", handleClickAway);
		};
	}, []);

	if (totalCount === 0) {
    return null;
	}

	return (
    <ContentPaper.Footer>
      <Stack direction="row" spacing={2} alignItems="center">
        <overviewForm.Field name="pageSize">
          {(fieldApi) => {
            if (fieldApi.state.value && !paginationOptions.some((option) => option.value === fieldApi.state.value)) {
              paginationOptions.push({ label: fieldApi.state.value.toString(), value: fieldApi.state.value });
            }

            if (totalCount && !paginationOptions.some((option) => option.value === totalCount)) {
              paginationOptions.push({ label: "vše", value: totalCount });
            }

            const getLabel = () => {
              if (!fieldApi.state.value) {
                return;
              }

              if (fieldApi.state.value >= totalCount) {
                return paginationOptions.find((option) => option.value === totalCount)?.label;
              }

              return paginationOptions.find((option) => option.value === fieldApi.state.value)?.label;
            };

            const label = getLabel();

            return (
              <overviewForm.Field name="page">
                {(pageApi) => {
                  const handleClick = (rowsPerPage: number) => {
                    pageApi.setValue(0);
                    fieldApi.setValue(rowsPerPage);
                    handleClose();
                  };
                  return (
                    <>
                      <Typography variant="body2" color="textSecondary" component="span">
                        výsledků na stránku:
                      </Typography>
                      <Box overflow="visible" position="relative">
                        <Paper
                          ref={menuRef}
                          sx={{
                            position: "absolute",
                            display: open ? "block" : "none",
                            zIndex: 9999,
                            bottom: "100%", // Position the Paper above the button
                            left: 0, // Align Paper to the left of the button
                            marginBottom: 1, // Optional: adds some space between the button and menu
                          }}
                        >
                          {paginationOptions
                            .sort((a, b) => a.value - b.value)
                            .filter((option) => option.value <= totalCount)
                            .filter((option, index, self) => {
                              // Keep the first occurrence of each value or if label is "vše"
                              const firstOccurrence = self.findIndex((t) => t.value === option.value);
                              return firstOccurrence === index || option.label === "vše";
                            })
                            .map((option) => (
                              <MenuItem
                                sx={(theme) => ({
                                  ...((option.value === fieldApi.state.value ||
                                    ((!fieldApi.state.value || fieldApi.state.value >= totalCount) &&
                                      option.value === totalCount)) && {
                                    backgroundColor: theme?.vars?.palette.info.light,
                                  }),
                                })}
                                key={option.label}
                                onClick={() => handleClick(option.value)}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                        </Paper>
                        <Tooltip arrow={true} title="změnit počet výsledků na stránku">
                          <Button
                            ref={buttonRef}
                            sx={(theme) => ({
                              textTransform: "none",
                              position: "relative",
                              border: `1px solid ${theme?.vars?.palette.grey[400]}`,
                              backgroundColor: theme?.vars?.palette.background.paper,
                              "&:hover": {
                                backgroundColor: theme.palette.grey[100],
                              },
                            })}
                            onClick={handleOpen}
                            color="inherit"
                            loading={!label}
                          >
                            {label}
                          </Button>
                        </Tooltip>
                      </Box>
                    </>
                  );
                }}
              </overviewForm.Field>
            );
          }}
        </overviewForm.Field>
        <overviewForm.Field name="page">
          {(fieldApi) => {
            return (
              <overviewForm.Subscribe selector={(state) => state.values.pageSize}>
                {(pageSizeValue) => {
                  const pageSize = pageSizeValue && pageSizeValue >= totalCount ? totalCount : (pageSizeValue ?? 0);

                  const page = (fieldApi.state.value ?? 0) + 1;

                  const displayedResultsFrom = page * (pageSize ?? 0) - (pageSize ?? 0) + 1;
                  const displayedResultsTo = page * (pageSize ?? 0);

                  const handlePrev = () => {
                    if (fieldApi.state.value === undefined) {
                      return;
                    }

                    if (pageSize === 0) {
                      return;
                    }

                    if (pageSize > 0 && totalCount / pageSize > 1) {
                      if (page > 1) {
                        fieldApi.setValue(page - 2);
                      } else {
                        fieldApi.setValue(Math.ceil(totalCount / pageSize) - 1);
                      }
                    }
                  };

                  const handleNext = () => {
                    if (fieldApi.state.value === undefined) {
                      return;
                    }

                    if (pageSize === 0) {
                      return;
                    }

                    if (pageSize > 0 && totalCount / pageSize > 1) {
                      if (page < Math.ceil(totalCount / pageSize)) {
                        fieldApi.setValue(page);
                      } else {
                        fieldApi.setValue(0);
                      }
                    }
                  };

                  return (
                    <>
                      <Typography variant="body2" color="textSecondary" component="span">
                        zobrazené výsledky: {displayedResultsFrom}-{displayedResultsTo} z {totalCount}
                      </Typography>
                      <Tooltip arrow={true} title="předchozí stránka">
                        <IconButton disabled={totalCount === 0 || pageSize === 0} onClick={handlePrev} size="small">
                          <ArrowBackIosNew />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow={true} title="další stránka">
                        <IconButton disabled={totalCount === 0 || pageSize === 0} onClick={handleNext} size="small">
                          <ArrowForwardIos />
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                }}
              </overviewForm.Subscribe>
            );
          }}
        </overviewForm.Field>
      </Stack>
    </ContentPaper.Footer>
	);
};
